import * as React from 'react';
import * as classes from './contract-info.scss';

// Models
import { OptionsViewModel, AddressViewModel } from '@models/common.models';
import { CustomerViewModel } from '@models/Customer';
import { RealEstateAgentViewModel } from '@models/RealEstateAgent';
import { RealEstateOfficeViewModel, RealEstateTerritoryViewModel } from '@models/RealEstateOffice';
import { SimpleEntityViewModel, NoRenewViewModel, ContractOrderType } from '@models/Contract';
import { EscrowOfficerViewModel } from '@models/EscrowOfficer';
import { EscrowOfficeViewModel } from '@models/EscrowOffice';
import { Role, Roles } from '@models/Roles';

// Components
import { ModalProps, Card, CardHeader, CardBody } from '@common/components';
import { Customer } from '../../../customer';
import { PropertyManagerSearchViewModel } from '@models/PropertyManager';
import { PriceAdjustmentViewModel, ProductViewModel, EmployeeDiscountViewModel } from '@models/Product';

import { dateParser } from '@common/dateParser';
import { EditLink } from '@common/components/wizard/_components/wizard-edit-link.component';
import { TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';
import { CoverageOption } from '@models/CoverageOption';

export interface ContractInfoProps {
  readonly jump?: (step: number) => void | undefined;
  readonly id?: number;
  readonly contactName: string;
  readonly contactType: OptionsViewModel;
  readonly contactMedium: OptionsViewModel;
  readonly contactInit: OptionsViewModel;
  readonly product: ProductViewModel;
  readonly coverageOptions: Array<CoverageOption>;
  readonly productGroup: OptionsViewModel;
  readonly priceAdjustment: PriceAdjustmentViewModel;
  readonly priceAdjustmentAmount: number;
  readonly employeeDiscount?: EmployeeDiscountViewModel;
  readonly totalPrice: number;
  readonly originalPrice: number;
  readonly propertyAddress: AddressViewModel;
  readonly propertyType: OptionsViewModel;
  readonly yearBuilt?: string;
  readonly propertySaleType: OptionsViewModel;
  readonly buyerCustomer: CustomerViewModel;
  readonly sellerCustomer: CustomerViewModel;
  readonly propertyManager: PropertyManagerSearchViewModel;
  readonly initAgent: RealEstateAgentViewModel;
  readonly initOffice: RealEstateOfficeViewModel;
  readonly coopAgent: RealEstateAgentViewModel;
  readonly coopOffice: RealEstateOfficeViewModel;
  readonly initSam: RealEstateTerritoryViewModel;
  readonly coopSam: RealEstateTerritoryViewModel;
  readonly escrowOfficer: EscrowOfficerViewModel;
  readonly transCoordinator?: TransactionCoordinatorViewModel;
  readonly escrowOffice: EscrowOfficeViewModel;
  readonly assetManager: OptionsViewModel;
  readonly additionalTxCoordinators: Array<SimpleEntityViewModel>;
  readonly additionalREAgents: Array<SimpleEntityViewModel>;
  readonly onBuyerCustomerSelect: ((model: string, value: number) => void) | null;
  readonly onSellerCustomerSelect: ((model: string, value: number) => void) | null;
  readonly toggleModal: ((modalProps?: ModalProps) => void) | null;
  readonly isRenewal?: boolean;
  readonly renewalAsIs?: boolean;
  readonly contractNote?: string;
  readonly contractStatus?: string;
  readonly totalAdjustment?: number;
  readonly contractNumber?: string;
  readonly noRenew: NoRenewViewModel;
  readonly noRenewReasons: OptionsViewModel;
  readonly coeDate?: Date;
  readonly orderType?: ContractOrderType;
  readonly orderDate?: Date;
  readonly hasSellersCoverage?: boolean;
}

// Page definition
export class ContractInfoComponent extends React.Component<ContractInfoProps> {
  showBuyerCustomerModal = () => {
    if (this.props.toggleModal === null) {
      return;
    }

    this.props.toggleModal({
      title: 'Edit Buyer/Owner Customer',
      component: Customer,
      saveCallback: this.loadBuyerOwner,
      childProps: { entityId: this.props.buyerCustomer.id }
    });
  };

  loadBuyerOwner = (id: number) => {
    if (this.props.toggleModal === null || this.props.onBuyerCustomerSelect === null) {
      return;
    }

    this.props.toggleModal();
    this.props.onBuyerCustomerSelect('', id);
  };

  showSellerCustomerModal = () => {
    if (this.props.toggleModal === null) {
      return;
    }

    this.props.toggleModal({
      title: 'Edit Seller Customer',
      component: Customer,
      saveCallback: this.loadSellerOwner,
      childProps: { entityId: this.props.sellerCustomer.id }
    });
  };

  loadSellerOwner = (id: number) => {
    if (this.props.toggleModal === null || this.props.onSellerCustomerSelect === null) {
      return;
    }
    this.props.toggleModal();
    this.props.onSellerCustomerSelect('', id);
  };

  renderAddress(address: AddressViewModel) {
    if (!address) {
      return;
    }
    return (
      <>
        {address.line1 ? (
          <>
            <h5>{address.line1}</h5>
            {address.line2 ? <h5>{address.line2}</h5> : ''}
            <h5>{address.city + ', ' + (address.state ? address.state.abbreviation : '--') + ' ' + address.zip}</h5>
          </>
        ) : (
          <h5>--</h5>
        )}
      </>
    );
  }

  renderCustomer(heading: string, customer: CustomerViewModel) {
    if (!customer) {
      return (
        <div className="col-md-12">
          <div>
            <label>No {heading} information</label>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="col-md-6">
          <label>{heading}</label>
          <h5>
            <strong>{customer.name || '--'}</strong>
          </h5>
        </div>
        <div className="col-md-6">
          <label>Email</label>
          <h5 className="ellipsis">{customer.emailAddress || '--'}</h5>
        </div>
        <div className="col-md-6">
          <label>Phone</label>
          <h5>{customer.phone || '--'}</h5>
        </div>
        <div className="col-md-12">
          <label>Mailing Address</label>
          {customer.addresses && customer.addresses.length > 0 ? this.renderAddress(customer.addresses[0]) : ''}
        </div>
      </>
    );
  }

  renderPropertyManager(heading: string, propertyManager: PropertyManagerSearchViewModel) {
    if (!propertyManager) {
      return (
        <div className="col-md-12">
          <div>
            <label>No {heading} information</label>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="col-md-6">
          <label>{heading}</label>
          <h5>
            <strong>{propertyManager.name}</strong>
          </h5>
        </div>
        <div className="col-md-6">
          <label>Email</label>
          <h5 className="ellipsis">{propertyManager.emailAddress}</h5>
        </div>
        <div className="col-md-6">
          <label>Phone</label>
          <h5>{propertyManager.phone}</h5>
        </div>
      </>
    );
  }

  renderRealEstateAgent(heading: string, agent: RealEstateAgentViewModel, office: RealEstateOfficeViewModel) {
    if (!agent) {
      return (
        <div className="col-md-12">
          <div>
            <label>No {heading} information</label>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="col-md-6">
          <label>{heading}</label>
          <h5>{agent.agentType ? agent.agentType.value : '--'}</h5>
          <h5>
            <strong>{agent.name}</strong>
          </h5>
        </div>
        <div className="col-md-6">
          <label>Email</label>
          <h5 className="ellipsis">{agent.emailAddress || '--'}</h5>
        </div>
        <div className="col-md-6">
          <label>Phone</label>
          <h5>{agent.directPhone || '--'}</h5>
        </div>
        <div className="col-md-6">
          <label>Fax</label>
          <h5>{agent.directFax || '--'}</h5>
        </div>
        {office && (
          <div className="col-md-12">
            <label>{heading} Office</label>
            <h5>
              <strong>{office.name}</strong>
            </h5>
            {this.renderAddress(office.address)}
          </div>
        )}
      </>
    );
  }

  renderSam(initSam: RealEstateTerritoryViewModel, coopSam: RealEstateTerritoryViewModel) {
    if (!initSam && !coopSam) {
      return (
        <div className="col-md-12">
          <div>
            <label>No Sales Executive Information</label>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="col-xs-9">
          <h4>Sales Executive Information</h4>
        </div>
        <div className="col-xs-12 mb-10 pb-5">
          <div className="row">
            {this.props.initSam ? (
              <div className="col-md-6">
                <label>Initiating Sales Executive</label>
                <h5>
                  <strong>{initSam.name ? initSam.name : '--'}</strong>
                </h5>
                <label>Area</label>
                <h5>{initSam.area ? initSam.area : '--'}</h5>
              </div>
            ) : (
              <h5>No Initiating Sales Executive Information</h5>
            )}
            {this.props.coopSam ? (
              <div className="col-md-6">
                <label>Cooperating Sales Executive</label>
                <h5>
                  <strong>{coopSam.name ? coopSam.name : '--'}</strong>
                </h5>
                <label>Area</label>
                <h5>{coopSam.area ? coopSam.area : '--'}</h5>
              </div>
            ) : (
              <h5>No Cooperating Sales Executive Information</h5>
            )}
          </div>
        </div>
      </>
    );
  }

  renderEscrowOfficer(
    heading: string,
    officer: EscrowOfficerViewModel | undefined,
    office: EscrowOfficeViewModel | undefined,
    coe?: Date
  ) {
    if (!officer) {
      return (
        <>
          <div className="col-md-6">
            <h5>No {heading} information</h5>
          </div>
          {coe && (
            <div className="col-md-6">
              <label>Est. COE</label>
              <h5>{coe ? dateParser.parse(coe).format('MM/DD/YYYY') : '--'}</h5>
            </div>
          )}
        </>
      );
    }
    return (
      <>
        <div className="col-md-6">
          <label>{heading}</label>
          <h5>
            <strong>{officer.name || '--'}</strong>
          </h5>
          <h5>{officer.officerType ? officer.officerType.value : ''}</h5>
        </div>
        <div className="col-md-6">
          <label>Est. COE</label>
          <h5>{coe ? dateParser.parse(coe).format('MM/DD/YYYY') : '--'}</h5>
        </div>
        <div className="col-md-6">
          <label>Email</label>
          <h5 className="ellipsis">{officer.emailAddress || '--'}</h5>
        </div>
        <div className="col-md-6">
          <label>Phone</label>
          <h5>{officer.directPhone || '--'}</h5>
        </div>
        {office && (
          <div className="col-md-6">
            <label>{heading} Company</label>
            <h5>
              <strong>{office.company || '--'}</strong>
            </h5>
            {this.renderAddress(office.address)}
          </div>
        )}
        <div className="col-md-6">
          <label>Fax</label>
          <h5>{officer.directFax || '--'}</h5>
        </div>
      </>
    );
  }

  renderTC(tc: TransactionCoordinatorViewModel) {
    return (
      <>
        <div className="col-md-6">
          <label>Transaction Coordinator</label>
          <h5>
            <strong>{tc.name}</strong>
          </h5>
        </div>
        <div className="col-md-6">
          <label>Email</label>
          <h5 className="ellipsis">{tc.emailAddress}</h5>
        </div>
        <div className="col-md-6">
          <label>Phone</label>
          <h5>{tc.directPhone}</h5>
        </div>
        <div className="col-md-6">
          <label>Fax</label>
          <h5>{tc.directFax}</h5>
        </div>
      </>
    );
  }

  renderSimpleEntity(heading: string, entity: SimpleEntityViewModel) {
    if (!entity) {
      return (
        <div className="col-md-12">
          <div>
            <label>No {heading} information</label>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="col-md-6">
          <label>{heading}</label>
          <h5>
            <strong>{entity.name}</strong>
          </h5>
        </div>
        <div className="col-md-6">
          <label>Email</label>
          <h5 className="ellipsis">{entity.email}</h5>
        </div>
        <div className="col-md-6">
          <label>Phone</label>
          <h5>{entity.phone}</h5>
        </div>
        <div className="col-md-6">
          <label>Fax</label>
          <h5>{entity.fax}</h5>
        </div>
      </>
    );
  }

  renderPriceAdjustment() {
    if (this.props.totalAdjustment !== undefined) {
      const inc = this.props.totalAdjustment >= 0 ? '' : '-';
      return (
        <h5>
          {inc} ${Math.abs(this.props.totalAdjustment).toFixed(2)}{' '}
          {this.props.priceAdjustment.id === 0
            ? ' '
            : this.props.priceAdjustment.id === 134 || this.props.priceAdjustment.id === 132
              ? 'RE Agent' +
                (this.props.employeeDiscount && this.props.employeeDiscount.value
                  ? ' + ' + this.props.employeeDiscount.value
                  : '')
              : this.props.priceAdjustment.value}
        </h5>
      );
    }
    return (
      <h5>
        {this.props.priceAdjustmentAmount
          ? '$ ' + Math.abs(+this.props.priceAdjustmentAmount).toFixed(2) + this.props.priceAdjustment.value
          : '$0'}
      </h5>
    );
  }

  renderTotalPrice() {
    return (
      <h4 className="purple-text">
        Total Price {this.props.totalPrice ? '$' + this.props.totalPrice.toFixed(2) : '$0'}
      </h4>
    );
  }

  renderNoRenewInfo() {
    if (this.props.id != null && this.props.id > 0) {
      return (
        <div className="row">
          <div className="col-xs-12 col-md-4">
            <label>No Renew?</label>
            <h5>
              <i
                className={
                  this.props.noRenew ? (this.props.noRenew.noRenewCd ? 'fa fa-check' : 'fa fa-times') : 'fa fa-times'
                }
              />
            </h5>
          </div>
          <div className="col-xs-12 col-md-4">
            <label>No Renew Date</label>
            <h5>
              {this.props.noRenew && this.props.noRenew.noRenewDate
                ? dateParser.parse(this.props.noRenew.noRenewDate).format('MM/DD/YYYY')
                : '--'}
            </h5>
          </div>
          <div className="col-xs-12 col-md-4">
            <label>No Renew Reason</label>
            <h5>{this.props.noRenew && this.props.noRenew.noRenewCd > 0 ? this.props.noRenew.noRenewDs : '--'}</h5>
          </div>
        </div>
      );
    }
    return;
  }

  renderEditLink(step: number) {
    return (
      !this.props.id &&
      !this.props.renewalAsIs && (
        <>
          &nbsp;
          <EditLink step={step} jump={this.props.jump} />
        </>
      )
    );
  }

  renderContactProperty() {
    return (
      <Card>
        <CardHeader>
          <div className="row m-3">
            <div className="col-xs-12">
              <h3>Contact & Property Information</h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="row">
            <div className="col-lg-6">
              <div className={classes.summaryCard}>
                <div className="col-xs-9">
                  <h4>
                    Contact Information
                    {this.renderEditLink(0)}
                  </h4>
                </div>

                <div className="col-xs-12 col-md-6">
                  <label>Caller's Name</label>
                  <h5>{this.props.contactName}</h5>
                </div>
                <div className="col-xs-12 col-md-6">
                  <label>Contacted By</label>
                  <h5>{this.props.contactType ? this.props.contactType.value : '--'}</h5>
                </div>
                <div className="col-xs-12 col-md-6">
                  <label>Contacted Via</label>
                  <h5>{this.props.contactMedium ? this.props.contactMedium.value : '--'}</h5>
                </div>
                <div className="col-xs-12 col-md-6">
                  <label>Contact Represents</label>
                  <h5>{this.props.contactInit ? this.props.contactInit.value : '--'}</h5>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className={classes.summaryCard}>
                <div className="col-xs-9">
                  <h4>
                    Property Information
                    {this.renderEditLink(0)}
                  </h4>
                </div>

                <div className="col-md-12">
                  <label>Property Address</label>
                  {this.renderAddress(this.props.propertyAddress)}
                </div>
                <div className="col-md-6 ">
                  <label>Property Type</label>
                  <h5>{this.props.propertyType ? this.props.propertyType.value : '--'}</h5>
                </div>
                <div className="col-md-6 ">
                  <label>Year Built</label>
                  <h5>{this.props.yearBuilt ? this.props.yearBuilt : '--'}</h5>
                </div>
                <div className="col-md-6">
                  <label>Sale Type</label>
                  <h5>{this.props.propertySaleType ? this.props.propertySaleType.value : '--'}</h5>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  renderCoverageCustomer() {
    const standardCoverageOption = this.props.coverageOptions[0];
    return (
      <Card defaultClosed={true}>
        <CardHeader>
          <div className="row m-3">
            <div className="col-xs-12">
              <h3>Coverage & Customer Information</h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="row">
            <div className="col-lg-12">
              <div className={[classes.summaryCard, 'row'].join(' ')}>
                <div className="col-xs-9">
                  <h4>
                    Coverage Information
                    {this.renderEditLink(1)}
                  </h4>
                </div>

                <div className="col-xs-12 col-md-4 ">
                  <label>Product</label>
                  <h5>
                    {this.props.id
                      ? this.props.product.productDesc
                        ? this.props.product.value + ' ' + this.props.product.productDesc
                        : this.props.product.value
                      : this.props.product.value}
                  </h5>
                </div>
                <div className="col-xs-12 col-md-4 ">
                  <label>Original Price</label>
                  <h5>
                    {this.props.coverageOptions && this.props.coverageOptions.length > 0
                      ? '$' + this.props.coverageOptions[0].price.toFixed(2)
                      : '$0'}
                  </h5>
                </div>
                <div className="col-xs-12 col-md-4 ">
                  <label>Coverage Options</label>
                  <div>
                    {this.props.coverageOptions.map(
                      (o, i) =>
                        i > 0 && (
                          <p className="mt-10 pt-5" key={o.id}>
                            {`${o.value}: ${o.price || o.sellersPrice ? '$' + (o.price || o.sellersPrice) : 'N/A'}`}
                          </p>
                        )
                    )}
                    {this.props.coverageOptions &&
                    this.props.coverageOptions.length > 0 &&
                    this.props.coverageOptions[0].sellersPrice ? (
                      <p className="mt-10 pt-5">
                        Total Seller Price: $
                        {standardCoverageOption.perDiemPrice &&
                        this.props.coeDate &&
                        this.props.orderDate &&
                        this.props.hasSellersCoverage
                          ? (
                              standardCoverageOption.sellersPrice *
                              dateParser.diffDate(this.props.coeDate as Date, this.props.orderDate as Date, 'days')
                            ).toFixed(2)
                          : standardCoverageOption.perDiemPrice && !this.props.coeDate && this.props.hasSellersCoverage
                            ? (standardCoverageOption.sellersPrice * 180).toFixed(2)
                            : '0.00'}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="col-xs-12 col-md-4 ">
                  <label>Group</label>
                  <h5>{this.props.productGroup ? this.props.productGroup.value : '--'}</h5>
                </div>
                <div className="col-xs-12 col-md-6 ">
                  <label>Price Adjustment</label>
                  {this.renderPriceAdjustment()}
                </div>
                <div className="col-md-2 text-right">
                  <div className="">{this.renderTotalPrice()}</div>
                </div>
                <div className="col-xs-12">{this.renderNoRenewInfo()}</div>
              </div>
            </div>
            <div className="col-lg-12">
              <div className={[classes.summaryCard, 'row', 'dark-gray-bc'].join(' ')}>
                <div className="col-xs-9">
                  <h4>
                    Customer Information
                    {!this.props.isRenewal && this.renderEditLink(2)}
                  </h4>
                </div>

                <div className="col-xs-12 mb-10 pb-5">
                  {Role.CheckRoles([Roles.Admin, ...Role.GetAllAccounting(), ...Role.GetAllService()]) && (
                    <div className="row">
                      <div className="col-xs-12 text-right">
                        {!this.props.isRenewal &&
                          !this.props.jump &&
                          this.props.buyerCustomer && (
                            <button className="btn btn-primary" type="button" onClick={this.showBuyerCustomerModal}>
                              Edit Buyer
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                  <div className="row">{this.renderCustomer('Buyer/Owner', this.props.buyerCustomer)}</div>
                </div>
                <div className="col-xs-12 mb-10 pb-5">
                  {Role.CheckRoles(
                    [Roles.Admin, ...Role.GetAllIBS(), ...Role.GetAllAccounting(), ...Role.GetAllService()]
                  ) && (
                    <div className="row">
                      <div className="col-xs-12 text-right">
                        {!this.props.isRenewal &&
                          !this.props.jump &&
                          this.props.sellerCustomer && (
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={this.showSellerCustomerModal}
                              disabled={!this.props.sellerCustomer.id}
                            >
                              Edit Seller
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                  {this.props.sellerCustomer && this.props.sellerCustomer.id ? (
                    <div className="row">{this.renderCustomer('Seller', this.props.sellerCustomer)}</div>
                  ) : (
                    <h5>No Seller Information</h5>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  renderAffiliates() {
    return (
      <Card defaultClosed={true}>
        <CardHeader>
          <div className="row m-3">
            <div className="col-xs-12">
              <h3>Affiliates Information</h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="row">
            {!this.props.isRenewal && (
              <div className="col-lg-12">
                <div className={[classes.summaryCard, 'row'].join(' ')}>
                  {this.props.id ? this.renderSam(this.props.initSam, this.props.coopSam) : ''}
                  <div className="col-xs-9">
                    <h4>
                      Realty Information
                      {this.renderEditLink(3)}
                    </h4>
                  </div>

                  <div className="col-xs-12 mb-10 pb-5">
                    <div className="row">
                      {this.props.initAgent ? (
                        this.renderRealEstateAgent('Initiating Agent', this.props.initAgent, this.props.initOffice)
                      ) : (
                        <h5>No Initiating Agent Information</h5>
                      )}
                    </div>
                  </div>
                  <div className="col-xs-12 mb-10 pb-5">
                    <div className="row">
                      {this.props.coopAgent && this.props.coopAgent.id ? (
                        this.renderRealEstateAgent('Cooperating Agent', this.props.coopAgent, this.props.coopOffice)
                      ) : (
                        <h5>No Cooperating Agent Information</h5>
                      )}
                    </div>
                  </div>
                  {this.props.additionalREAgents.length > 0 && (
                    <>
                      {this.props.additionalREAgents.map(ra => {
                        return (
                          <div className="col-xs-12 mb-10 pb-5" key={ra.id}>
                            <div className="row">{this.renderSimpleEntity('Additional Real Estate Agent', ra)}</div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}
            {!this.props.isRenewal && (
              <div className="col-lg-12">
                <div className={[classes.summaryCard, 'row', 'dark-gray-bc'].join(' ')}>
                  <div className="col-xs-9">
                    <h4>
                      Escrow Information
                      {this.renderEditLink(3)}
                    </h4>
                  </div>

                  <div className="col-xs-12 mb-10 pb-5">
                    <div className="row">
                      {this.renderEscrowOfficer(
                        'Escrow Officer',
                        this.props.escrowOfficer,
                        this.props.escrowOffice,
                        this.props.coeDate
                      )}
                    </div>
                  </div>
                  {this.props.transCoordinator && this.renderTC(this.props.transCoordinator)}
                  {this.props.additionalTxCoordinators.length > 0 && (
                    <>
                      {this.props.additionalTxCoordinators.map(tc => {
                        return (
                          tc && (
                            <div className="col-xs-12 mb-10 pb-5" key={tc.id}>
                              <div className="row">{this.renderSimpleEntity('Transaction Coordinator', tc)}</div>
                            </div>
                          )
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="col-lg-12">
              <div className={[classes.summaryCard, 'row'].join(' ')}>
                <>
                  <div className="col-xs-9">
                    <h4>
                      Property Manager Information{' '}
                      {!this.props.id && <>{this.props.isRenewal ? this.renderEditLink(2) : this.renderEditLink(3)}</>}
                    </h4>
                  </div>

                  <div className="col-xs-12 mb-10 pb-5">
                    <div className="row">
                      {this.props.propertyManager && this.props.propertyManager.id ? (
                        this.renderPropertyManager('Property Manager', this.props.propertyManager)
                      ) : (
                        <h5>No Property Manager Information</h5>
                      )}
                    </div>
                  </div>
                </>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  render() {
    return (
      <section className={classes.summary}>
        <div className="row p-10">
          {this.props.contractNumber ? (
            <h2>
              Contract # {this.props.contractNumber} {this.props.isRenewal ? '- Renewal' : ''}
            </h2>
          ) : (
            ''
          )}
          {}

          {this.props.contractNote && <h3>Special Note: {this.props.contractNote}</h3>}
          <div className="col-md-12">
            {this.renderContactProperty()}
            {this.renderCoverageCustomer()}

            {this.renderAffiliates()}
          </div>
        </div>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/contract-info/contract-info.component.tsx